import moment from 'moment';
import {
    isValidElement,
    useContext,
    type ChangeEvent,
    type ComponentProps,
    type HTMLAttributes,
    type ReactElement,
    type ReactNode,
} from 'react';
import type { Value } from 'react-phone-number-input';
import type { GroupBase } from 'react-select';
import type { TagSelectorProps } from '~/admin/components/TagSelector';
import TagSelector from '~/admin/components/TagSelector';
import {
    ChipCheckGroup,
    ChipRadioGroup,
    type ChipChoiceOption,
    type ChipRadioGroupProps,
} from '~/components/new/Chip';
import ChipSearchBlock, { type ChipsAndSearchProps } from '~/components/new/ChipsSearchBlock';
import ControlCard from '~/components/ui/ControlCard';
import { formContext, PhoneInput, Textarea, type Form as FormType } from '~/components/ui/Form';
import CheckBox, { CheckBoxInput, type CheckBoxProps } from '~/components/ui/forms/CheckBox';
import Choice, { RadioInput, YesOrNo, type ChoiceProps } from '~/components/ui/forms/Choice';
import DatePicker, { type DatePickerProps } from '~/components/ui/forms/DatePicker';
import Input, { PhoneInputProps, type InputProps } from '~/components/ui/forms/Input';
import Select, { type SelectOption, type SelectProps } from '~/components/ui/forms/Select';
import { TimeSelect } from '~/components/ui/forms/TimeSelect.tsx';
import type { LabelProps } from '~/components/ui/Label';
import Label from '~/components/ui/Label';
import Switch, { type SwitchProps } from '~/components/ui/Switch';
import type { TextEditorProps } from '~/components/ui/TextEditor';
import TextEditor from '~/components/ui/TextEditor';
import { SelectTimezone } from '~/job_positions/SelectTimezone';
import { ucfirst } from '~/utils/format.ts';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import type { TextareaProps } from './Textarea';
export { default as LinkOrFileField } from './LinkOrFileField';

export type FieldProps = HTMLAttributes<HTMLDivElement> & {
    children: ReactNode;
    size?: string;
    inline?: boolean;
    className?: string;
};

export function Field({ children, size, inline = false, className = '', ...props }: FieldProps) {
    return (
        <div
            {...props}
            className={classNames({
                'form-field': true,
                'form-field-inline': inline,
                [className]: Boolean(className),
            })}
            style={{ maxWidth: size ? size : 'auto' }}>
            {children}
        </div>
    );
}

export type FieldHelpProps = {
    children: ReactNode;
    className?: string;
};

export function FieldHelp({ children, className = '' }: FieldHelpProps) {
    return (
        <div className={classNames({ 'form-help': true, [className]: Boolean(className) })}>
            {children}
        </div>
    );
}

export type FieldInfoProps = {
    children: ReactNode;
    className?: string;
};

export function FieldInfo({ children, className = '' }: FieldInfoProps) {
    return (
        <p className={classNames({ 'form-info': true, [className]: Boolean(className) })}>
            {children}
        </p>
    );
}

export type FieldErrorProps = {
    children: ReactNode;
};

export function FieldError({ children }: FieldErrorProps) {
    return (
        <div
            className='form-error'
            data-test-component='ui/forms/FieldError'>
            {children}
        </div>
    );
}

export type FieldLabelProps = Omit<LabelProps, 'caption'> & {
    htmlFor?: string;
    disabled?: boolean;
    optional?: boolean;
} & DataTestAttributes;
export function FieldLabel({
    htmlFor,
    optional,
    disabled,
    ['data-test-component']: testComponent = 'ui/forms/FieldLabel',
    ['data-test-name']: testName,
    ...props
}: FieldLabelProps) {
    return (
        <label
            data-test-component={testComponent}
            data-test-name={testName}
            className={classNames({
                'control-label': true,
                'control-label-disabled': Boolean(disabled),
            })}
            onClick={(e) => {
                if (props.onToggle) {
                    e.stopPropagation();
                    e.preventDefault();
                    if (!disabled) {
                        props.onToggle?.(!props.toggleValue);
                    }
                }
            }}
            htmlFor={htmlFor}>
            <Label
                {...props}
                data-test-name={testName}
                caption={optional ? '(optional)' : ''}
                toggleDisabled={Boolean(disabled)}
            />
        </label>
    );
}

type FieldLabelType = ReactElement<typeof FieldLabel>;

export type BaseFieldProps = {
    form?: FormType<any, any>;
    name: string;
    label?: string | FieldLabelType;
    errorText?: string;
    help?: string | ReactNode;
    optional?: boolean;
    required?: boolean;
    maxLength?: number;
    disabled?: boolean | (() => boolean);
    children?: ReactNode;
    className?: string;
} & DataTestAttributes;

export function BaseField({
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional = false,
    disabled = false,
    children,
    className = '',
    maxLength,
    ['data-test-component']: testComponent = 'ui/forms/BaseField',
    ['data-test-name']: testName,
}: BaseFieldProps) {
    const form = useEffectiveForm(propForm);
    const finalDisabled = typeof disabled === 'function' ? disabled() : disabled;
    return (
        <Field
            className={classNames({
                'form-field-disabled': finalDisabled,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-name={testName ?? name}>
            {/* when label is a string, we use a pre-configure FieldLabel,
            otherwise we have a FieldLabel object and then we inject some props */}
            {typeof label === 'string' && (
                <FieldLabel
                    htmlFor={name}
                    optional={optional}
                    className={classNames({ 'control-label-disabled': Boolean(disabled) })}>
                    {label}
                </FieldLabel>
            )}{' '}
            {isValidElement(label) && (
                <FieldLabel
                    {...label.props}
                    optional={optional}
                    disabled={finalDisabled}
                    htmlFor={name}
                />
            )}
            {children}
            <div className='form-field-bottom-addon'>
                <div className='form-field-bottom-addon-left'>
                    {form.hasError(name) ? (
                        <FieldError>{ucfirst(form.getError(name) ?? errorText)}</FieldError>
                    ) : null}
                    {help && <FieldHelp>{help}</FieldHelp>}
                </div>
                <div className='form-field-bottom-addon-right'>
                    {maxLength !== undefined && (
                        <span className='form-input-max'>
                            {form.getField(name)?.length ?? 0}/{maxLength}
                        </span>
                    )}
                </div>
            </div>
        </Field>
    );
}

export type InputFieldProps = Omit<BaseFieldProps, 'className'> &
    Omit<InputProps, 'disabled' | 'form'> & {
        onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
        prefix?: string;
        suffix?: string;
        fieldClassName?: string;
    };

export function InputField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    fieldClassName,
    // other props
    disabled,
    maxLength,
    ['data-test-name']: testName,
    // Input props
    ...props
}: InputFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            maxLength={maxLength}
            required={required}
            className={fieldClassName}
            data-test-component='ui/forms/InputField'
            data-test-name={testName}>
            <Input
                {...props}
                maxLength={maxLength}
                name={name}
                id={name}
                value={form.getField(name) === null ? '' : form.getField(name)}
                onChange={props.onChange || form.handleInputChange(name, props.type === 'number')}
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                error={form.hasError(name)}
            />
        </BaseField>
    );
}

export type PhoneInputFieldProps = BaseFieldProps &
    Omit<PhoneInputProps, 'disabled' | 'form' | 'value' | 'onChange'> & {
        onChange?: (value?: Value) => void;
    };

export function PhoneInputField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled,
    ['data-test-name']: testName,
    // Input props
    ...props
}: PhoneInputFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component='ui/forms/PhoneInputField'
            data-test-name={testName}>
            <PhoneInput
                {...props}
                name={name}
                id={name}
                value={form.getField(name)}
                onChange={props.onChange || ((v) => form.handleChange(name, v))}
                disabled={
                    form.submitting ||
                    (typeof disabled === 'function' ? disabled() : disabled) ||
                    false
                }
                error={form.hasError(name)}
            />
        </BaseField>
    );
}

export type MoneyInputFieldProps = BaseFieldProps &
    Omit<InputProps, 'disabled' | 'form' | 'value' | 'onChange'> & {
        onChange?: (value: number) => void;
    };

export function MoneyInputField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled,
    ['data-test-component']: testComponent = 'ui/forms/MoneyInputField',
    ['data-test-name']: testName,
    // Input props
    ...props
}: MoneyInputFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component={testComponent}
            data-test-name={testName}>
            <Input.Money
                {...props}
                name={name}
                value={form.getField(name)}
                onChange={props.onChange ?? ((v: number) => form.handleChange(name, v))}
                disabled={
                    form.submitting ||
                    (typeof disabled === 'function' ? disabled() : disabled) ||
                    false
                }
                error={form.hasError(name)}
            />
        </BaseField>
    );
}

export type TextareaFieldProps = BaseFieldProps &
    TextareaProps & {
        onChange?: (e: any) => void;
    };

export function TextareaField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled,
    maxLength,
    ['data-test-component']: testComponent = 'ui/forms/TextareaField',
    ['data-test-name']: testName,
    // Textarea props
    ...props
}: TextareaFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            maxLength={maxLength}
            required={required}
            data-test-component={testComponent}
            data-test-name={testName}>
            <Textarea
                {...props}
                name={name}
                id={name}
                value={form.getField(name)}
                onChange={props.onChange || form.handleInputChange(name)}
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                error={form.hasError(name)}
            />
        </BaseField>
    );
}

export type SelectFieldProps<T extends string | number> = BaseFieldProps &
    Omit<SelectProps<T>, 'disabled' | 'isMulti'> & {
        isMulti?: never; // this belongs to SelectProps, SelectField uses 'multi' instead
        multi?: boolean;
        options?: SelectProps<T>['options'] | GroupBase<SelectOption<T>>[];
        onChange?: (v: any) => void;
        innerLabel?: string;
        readOnly?: boolean;
    };

export function getReadOnlySelectValue<T extends string | number>(
    value: unknown,
    options?: SelectFieldProps<T>['options'],
): string {
    const toDisplayString = (label: unknown): string =>
        typeof label === 'string' || typeof label === 'number' ? String(label) : '';

    const flatOptions = (options ?? []).flatMap((option) => {
        if (typeof option === 'string' || typeof option === 'number') {
            return [{ label: String(option), value: option as T }];
        }
        if (option && typeof option === 'object' && Object.hasOwn(option, 'options')) {
            return (option as GroupBase<SelectOption<T>>).options;
        }
        return [option as SelectOption<T>];
    });

    const toLabel = (optionValue: unknown): string => {
        if (optionValue === null || optionValue === undefined) return '';

        if (typeof optionValue === 'object') {
            if (Object.hasOwn(optionValue, 'label')) {
                return toDisplayString((optionValue as { label?: unknown }).label);
            }
            if (Object.hasOwn(optionValue, 'value')) {
                const primitiveValue = (optionValue as { value: T }).value;
                const matchedOption = flatOptions.find((opt) => opt.value === primitiveValue);
                return toDisplayString(matchedOption?.label) || String(primitiveValue);
            }
        }

        const matchedOption = flatOptions.find((opt) => opt.value === optionValue);
        if (toDisplayString(matchedOption?.label)) {
            return toDisplayString(matchedOption?.label);
        }
        if (typeof optionValue === 'string' || typeof optionValue === 'number') {
            return String(optionValue);
        }
        return '';
    };

    if (Array.isArray(value)) {
        return value
            .map(toLabel)
            .filter((item) => item !== '')
            .join(', ');
    }

    return toLabel(value);
}

export function SelectField<T extends string | number>({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled,
    readOnly = false,
    ['data-test-name']: testName,
    // Select props
    multi = false,
    innerLabel,
    ...props
}: SelectFieldProps<T>) {
    const form = useEffectiveForm(propForm);
    const readOnlyValue = getReadOnlySelectValue(form.getField(name), props.options);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component='ui/forms/SelectField'
            data-test-name={testName}>
            {readOnly && (
                <Input
                    name={name}
                    id={name}
                    value={readOnlyValue}
                    readOnly
                    disabled={
                        form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                    }
                    error={form.hasError(name)}
                />
            )}
            {!readOnly && multi && (
                <Select
                    {...props}
                    inputId={name}
                    value={form.getField(name) || undefined}
                    isMulti
                    onChange={
                        props.onChange ||
                        ((v) =>
                            form.handleChange(
                                name,
                                v.map((v) => v.value),
                            ))
                    }
                    disabled={
                        form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                    }
                    error={form.hasError(name)}
                    label={innerLabel}
                />
            )}
            {!readOnly && !multi && (
                <Select
                    {...props}
                    inputId={name}
                    value={form.getField(name) || undefined}
                    onChange={props.onChange || ((v) => form.handleChange(name, v?.value))}
                    disabled={
                        form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                    }
                    error={form.hasError(name)}
                    label={innerLabel}
                />
            )}
        </BaseField>
    );
}

export type TimeSelectFieldProps = BaseFieldProps &
    Omit<SelectProps<string>, 'disabled' | 'options'> & {
        onChange?: (v: any) => void;
        intervalMinutes?: number;
    };

export function TimeSelectField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled,
    // Select props
    intervalMinutes,
    ...props
}: TimeSelectFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}>
            <TimeSelect
                {...props}
                inputId={name}
                value={form.getField(name)}
                intervalMinutes={intervalMinutes}
                onChange={
                    props.onChange ||
                    ((v: SelectOption<string>) => form.handleChange(name, v.value))
                }
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                error={form.hasError(name)}
            />
        </BaseField>
    );
}

export type DatePickerFieldProps = BaseFieldProps &
    Omit<
        DatePickerProps,
        'disabled' | 'form' | 'showMonthYearDropdown' | 'selectsRange' | 'selectsMultiple'
    > & {
        onChange?: (v: any) => void;
        utc?: boolean; // handles date as string converting it to UTC format
    };

export function DatePickerField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    utc = false,
    // other props
    disabled,
    // DatePicker props
    id,
    ['data-test-component']: testComponent = 'ui/forms/DatePickerField',
    ['data-test-name']: testName,
    ...props
}: DatePickerFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component={testComponent}
            data-test-name={testName}>
            <DatePicker
                {...props}
                isClearable={optional}
                id={id ?? name}
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                value={form.getField(name)}
                onChange={
                    props.onChange ||
                    ((v: Date | null) => {
                        const safeValue =
                            v !== null && utc ? moment(v).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : v;
                        return utc
                            ? form.handleChange(name, safeValue)
                            : form.handleChange(name, safeValue);
                    })
                }
                error={form.hasError(name)}
            />
        </BaseField>
    );
}

export type ChoiceFieldProps = BaseFieldProps &
    Omit<ChoiceProps, 'disabled'> & {
        onChange?: (v: any) => void;
    };
export function ChoiceField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled = false,
    ['data-test-name']: testName,
    // Choice props
    ...props
}: ChoiceFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component='ui/forms/ChoiceField'
            data-test-name={testName}>
            <Choice
                {...props}
                name={name}
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                onChange={props.onChange || ((v) => form.handleChange(name, v))}
                value={form.getField(name)}
            />
        </BaseField>
    );
}

export type CheckBoxFieldProps = BaseFieldProps &
    Omit<CheckBoxProps, 'disabled'> & {
        onChange?: (v: boolean) => void;
    };
export function CheckBoxField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled = false,
    // CheckBox props
    ...props
}: CheckBoxFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}>
            <CheckBox
                {...props}
                label={label}
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                id={name}
                onChange={props.onChange || ((v) => form.handleChange(name, v))}
                value={form.getField(name)}
            />
        </BaseField>
    );
}

export type SwitchFieldProps = BaseFieldProps &
    Omit<SwitchProps, 'disabled' | 'value'> & {
        onChange?: (v: boolean) => void;
    };

export function SwitchField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled = false,
    // Switch props
    ...props
}: SwitchFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}>
            <Switch
                labelPosition='right'
                bordered
                {...props}
                label={label}
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                onChange={props.onChange || ((v) => form.handleChange(name, v))}
                value={form.getField(name)}
                id={name}
            />
        </BaseField>
    );
}

function useEffectiveForm(propForm?: FormType<any, any>) {
    const contextForm = useContext(formContext);
    const form = propForm || contextForm;
    if (!form) {
        throw new Error(
            'No form instance found. Please provide a form via props or wrap the field in a <Form> component.',
        );
    }
    return form;
}

export type ControlCardFieldProps = BaseFieldProps & {
    onChange?: (v: any) => void;
    options: { label: string; value: string | number }[];
    inputType?: 'radio' | 'checkbox';
} & DataTestAttributes;
export function ControlCardField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    ['data-test-component']: testComponent = 'ui/forms/ControlCardField',
    ['data-test-name']: testName,
    options,
    inputType = 'radio',
    className,
    // other props
    disabled = false,
}: ControlCardFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            className={className}
            data-test-component={testComponent}
            data-test-name={testName}>
            {options.map((opt) => (
                <ControlCard
                    key={opt.value}
                    {...opt}
                    title={opt.label}
                    inputComponent={
                        inputType === 'radio' ? (
                            <RadioInput
                                name={name}
                                checked={form.getField(name) === opt.value}
                            />
                        ) : (
                            <CheckBoxInput
                                name={name}
                                checked={
                                    Array.isArray(form.getField(name))
                                        ? form.getField(name).includes(opt.value)
                                        : false
                                }
                            />
                        )
                    }
                    disabled={
                        form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                    }
                    onClick={() => {
                        if (inputType === 'checkbox') {
                            const newValue = Array.isArray(form.data[name])
                                ? form.data[name].slice()
                                : [];
                            const index = newValue.indexOf(opt.value);
                            if (index > -1) {
                                newValue.splice(index, 1);
                            } else {
                                newValue.push(opt.value);
                            }
                            form.handleChange(name, newValue);
                        } else {
                            form.handleChange(name, opt.value);
                        }
                    }}
                />
            ))}
        </BaseField>
    );
}

type TextEditorFieldProps = Omit<BaseFieldProps, 'maxLength'> & TextEditorProps;
export function TextEditorField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    className,
    // other props
    disabled = false,
    // TextEditor props
    ...props
}: TextEditorFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={form.submitting}
            required={required}
            className={className}>
            <TextEditor
                {...props}
                value={form.getField(name)}
                onChange={(value) => form.handleChange(name, value)}
                disabled={form.submitting || disabled}
            />
        </BaseField>
    );
}

export type ChipRadioGroupFieldProps = BaseFieldProps &
    Omit<ChipRadioGroupProps, 'options'> & {
        onChange?: (v: any) => void;
        options: ChipChoiceOption[];
        multiSelect?: boolean;
        addOns?: ReactNode;
    };
export function ChipGroupField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    className,
    // other props
    disabled = false,
    multiSelect = false,
    addOns,
    // Choice props
    options,
    ['data-test-component']: testComponent = 'ui/forms/ChipGroupField',
    ['data-test-name']: testName,
    ...props
}: ChipRadioGroupFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            className={className}
            data-test-component={testComponent}
            data-test-name={testName}>
            {multiSelect ? (
                <ChipCheckGroup
                    className={classNames({
                        'chip-group-error': form.hasError(name),
                    })}
                    id={name}
                    options={
                        disabled
                            ? options.map((option) => ({ ...option, disabled: true }))
                            : options
                    }
                    values={form.getField(name) ?? []}
                    onChange={(v) => {
                        const currentValues = form.getField(name) ?? [];
                        const newValues = currentValues.includes(v.value)
                            ? currentValues.filter((val: any) => val !== v.value)
                            : [...currentValues, v.value];
                        if (props.onChange) {
                            props.onChange?.(newValues);
                        } else {
                            form.handleChange(name, newValues);
                        }
                    }}>
                    {addOns}
                </ChipCheckGroup>
            ) : (
                <ChipRadioGroup
                    className={classNames({
                        'chip-group-error': form.hasError(name),
                    })}
                    id={name}
                    options={
                        disabled
                            ? options.map((option) => ({ ...option, disabled: true }))
                            : options
                    }
                    value={form.getField(name)}
                    onChange={(v) => {
                        if (props.onChange) {
                            props.onChange?.(v.value);
                        } else {
                            form.handleChange(name, v.value);
                        }
                    }}>
                    {addOns}
                </ChipRadioGroup>
            )}
        </BaseField>
    );
}

export type ChipSearchFieldProps = BaseFieldProps &
    Omit<ChipsAndSearchProps, 'values'> & {
        onChange?: (v: any) => void;
    };

export function ChipSearchField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    className,
    // other props
    disabled = false,
    // Chips And Search props
    ...props
}: ChipSearchFieldProps) {
    const form = useEffectiveForm(propForm);

    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            className={className}
            data-test-component='ui/forms/ChipSearchField'>
            <ChipSearchBlock
                {...props}
                values={form.getField(name) ?? []}
                onChange={
                    props.onChange
                        ? () => props.onChange?.(form.data[name])
                        : (v) => form.handleChange(name, v)
                }
            />
        </BaseField>
    );
}

export type ToggleFieldProps = BaseFieldProps & LabelProps;
export function ToggleField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    disabled = false,
    ['data-test-component']: testComponent = 'ui/forms/ToggleField',
    ['data-test-name']: testName,
    // Toggle specific props
    onToggle,
    ...props
}: ToggleFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={
                <FieldLabel
                    {...props}
                    toggle
                    toggleValue={form.getField(name)}
                    onToggle={onToggle ?? ((v) => form.handleChange(name, v))}>
                    {label}
                </FieldLabel>
            }
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            data-test-name={testName}
            data-test-component={testComponent}
        />
    );
}

export type ToggleBoxFieldProps = Omit<BaseFieldProps, 'required' | 'maxLength'> &
    Omit<LabelProps, 'caption' | 'toggle' | 'toggleValue' | 'children'> & {
        framed?: boolean;
        onToggle?: (value: boolean) => void;
    };

export function ToggleBoxField({
    form: propForm,
    name,
    label,
    children,
    className = '',
    disabled = false,
    errorText,
    help,
    optional,
    description,
    framed = false,
    onToggle,
    ['data-test-component']: testComponent = 'ui/forms/ToggleBoxField',
    ['data-test-name']: testName,
    ...props
}: ToggleBoxFieldProps) {
    const form = useEffectiveForm(propForm);
    const enabled = Boolean(form.getField(name));

    return (
        <BaseField
            form={form}
            className={classNames({
                'framed rounded-4 p-4 gap-4': framed,
                'gap-2': !framed,
                [className]: Boolean(className),
            })}
            name={name}
            label={
                <FieldLabel
                    {...props}
                    description={description}
                    toggle
                    toggleValue={enabled}
                    onToggle={onToggle ?? ((value) => form.handleChange(name, value))}>
                    {label}
                </FieldLabel>
            }
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            data-test-component={testComponent}
            data-test-name={testName}>
            {enabled ? children : null}
        </BaseField>
    );
}

export type TagSelectorFieldProps = BaseFieldProps &
    Omit<TagSelectorProps, 'disabled' | 'value' | 'onChange'> & {
        onChange?: (v: any) => void;
    };
export function TagSelectorField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled = false,
    ['data-test-name']: testName,
    // TagSelector props
    ...props
}: TagSelectorFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component='ui/forms/TagSelectorField'
            data-test-name={testName}>
            <TagSelector
                {...props}
                inputId={name}
                value={form?.getField(name)}
                disabled={form?.submitting}
                onChange={(v) => {
                    if (Array.isArray(v)) {
                        v = v.map((tag) => (typeof tag === 'object' ? Number(tag.value) : tag));
                    }
                    form?.handleChange(name, v);
                }}
            />
        </BaseField>
    );
}

export type YesOrNoFieldProps = BaseFieldProps &
    Omit<ComponentProps<typeof YesOrNo>, 'disabled' | 'value' | 'onChange'> & {
        onChange?: (v: string | number | boolean) => void;
    };

export function YesOrNoField({
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    disabled = false,
    ['data-test-name']: testName,
    ...props
}: YesOrNoFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component='ui/forms/YesOrNoField'
            data-test-name={testName}>
            <YesOrNo
                {...props}
                name={name}
                disabled={
                    form.submitting || (typeof disabled === 'function' ? disabled() : disabled)
                }
                value={form.getField(name)}
                onChange={props.onChange || ((v) => form.handleChange(name, v))}
            />
        </BaseField>
    );
}

type SelectTimezoneFieldProps = BaseFieldProps &
    Omit<SelectProps<string>, 'disabled' | 'value' | 'onChange'> & {
        allZones?: boolean;
    };
export function SelectTimezoneField({
    // BaseField specific props
    form: propForm,
    name,
    label,
    errorText,
    help,
    optional,
    required,
    // other props
    disabled = false,
    ['data-test-name']: testName,
    // SelectTimezone props
    ...props
}: SelectTimezoneFieldProps) {
    const form = useEffectiveForm(propForm);
    return (
        <BaseField
            form={form}
            name={name}
            label={label}
            errorText={errorText}
            help={help}
            optional={optional}
            disabled={disabled}
            required={required}
            data-test-component='ui/forms/SelectTimezoneField'
            data-test-name={testName}>
            <SelectTimezone
                {...props}
                inputId={name}
                error={form.hasError(name)}
                value={form.getField(name)}
                disabled={form.submitting}
                onChange={(v: SelectOption<string>) => form.handleChange(name, v.value)}
            />
        </BaseField>
    );
}
